/**
 * Product catalog templates for product categories.
 *
 * Each template provides:
 *   - suggestedProducts: pre-filled PRODUCT items matching the ProductBuilder schema
 *   - coverage: UI-only object (BE support pending)
 *   - availability: UI-only object (BE support pending)
 *
 * Template keys must match category ids (P####) in StoreCategoryList.json.
 */

import { GenerateShortID } from "./StoreUtils";

const uid = () => `tpl_${GenerateShortID(6)}`;

const uidForVariant = () => `tpl_var_${GenerateShortID(6)}`;
const uidForOption = () => `tpl_opt_${GenerateShortID(6)}`;
const uidForCustomisation = () => `tpl_cust_${GenerateShortID(6)}`;

function makeVariant(name, type, options, isRequired = true) {
  return {
    variantId: uidForVariant(),
    name,
    type,
    isRequired,
    Options: options.map((option, i) => {
      const normalized = typeof option === "string" ? { label: option } : option;
      return {
        optionId: uidForOption(),
        label: normalized.label,
        price: Number(normalized.price) || 0,
        addonPrice: normalized.addonPrice ?? true,
        isDefault: normalized.isDefault ?? i === 0,
      };
    }),
  };
}

function makeCustomization(name, type, options = {}) {
  return {
    customisationId: uidForCustomisation(),
    name,
    type,
    price: options.price ?? 0,
    addonPrice: options.addonPrice ?? true,
    DropdownOptions: options.DropdownOptions ?? [],
    acceptedFileTypes: type === "file" ? options.acceptedFileTypes ?? [] : [],
  };
}

function makeProduct(name, description, basePrice, variants = [], customisations = []) {
  return {
    ItemId: uid(),
    ItemName: name,
    Description: description,
    BasePrice: basePrice,
    Discount: 0,
    ItemType: "PRODUCT",
    ItemImages: [],
    Variants: variants,
    Customisations: customisations,
  };
}

const DEFAULT_AVAILABILITY = {
  days: [],
};

export const PRODUCT_CATALOG_TEMPLATES = {
  "P1006": {
    suggestedProducts: [
      makeProduct(
        "Color Photo Print",
        "Color photo printing in postcard or A4 size.",
        16,
        [
          makeVariant("Print Size", "single", [
            { label: 'Postcard (4" x 6")', price: 16, addonPrice: false },
            { label: "A4", price: 25, addonPrice: false },
          ]),
          makeVariant("Paper GSM", "single", ["180 GSM"]),
        ],
        [
          makeCustomization("Upload Photo", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Print Instructions", "text"),
          makeCustomization("Contact Before Printing", "boolean"),
        ]
      ),
      makeProduct(
        "Sticker / Label Print",
        "Custom stickers or labels printed on sticker paper.",
        25,
        [makeVariant("Sheet Size", "single", [{ label: "A4", price: 25, addonPrice: false }])],
        [
          makeCustomization("Upload Sticker / Label Artwork", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Print Instructions", "text"),
        ]
      ),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: {
      type: "local",
      notes: "Document Collection, Local Pickup, Local Delivery",
    },
  },

  "P1007": {
    suggestedProducts: [
      makeProduct(
        "Engraved Photo with Stand",
        "Personalised engraved photo supplied with a display stand.",
        80,
        [makeVariant("Size", "single", [
          { label: 'Mini (3" x 3")', price: 80 },
          { label: 'Photo (3.5" x 5.5")', price: 120 },
        ])],
        [
          makeCustomization("Upload Photo for Engraving", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Engraving Text", "text"),
          makeCustomization("Confirm Design Before Production", "boolean"),
        ]
      ),
      makeProduct(
        "Engraved Bookmark",
        "Personalised engraved bookmark.",
        18,
        [],
        [
          makeCustomization("Upload Engraving Artwork", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Engraving Text", "text"),
        ]
      ),
      makeProduct(
        "Engraved Wooden Coaster Mini",
        "Personalised mini wooden coaster with laser engraving.",
        70,
        [],
        [
          makeCustomization("Upload Engraving Artwork", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Engraving Text", "text"),
        ]
      ),
      makeProduct(
        "DIY Cutout with Stand",
        "Laser-cut DIY craft kit supplied with a display stand.",
        60,
        [makeVariant("Size", "single", [
          { label: 'Small (3" x 6")', price: 60 },
          { label: 'Large (6" x 8")', price: 160 },
        ])],
        [
          makeCustomization("Upload Cutout Artwork", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Cutout Instructions", "text"),
        ]
      ),
      makeProduct(
        "Engraved Steel Pendant",
        "Personalised laser-engraved steel pendant.",
        100,
        [makeVariant("Style", "single", [
          { label: "Standard", price: 100 },
          { label: "Premium", price: 300 },
        ])],
        [
          makeCustomization("Upload Engraving Artwork", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Engraving Text", "text"),
          makeCustomization("Confirm Design Before Production", "boolean"),
        ]
      ),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: {
      type: "local",
      notes: "Personalised Orders, Local Pickup, Local Delivery",
    },
  },



};

export function hasProductTemplate(productSegment) {
  return Boolean(productSegment && PRODUCT_CATALOG_TEMPLATES[productSegment]);
}

export function getProductTemplate(productSegment) {
  return PRODUCT_CATALOG_TEMPLATES[productSegment] ?? null;
}
